/**
 * React Query hooks for User Profile
 * 
 * Handles profile fetching and updates for the current user.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { profileApi, type ProfileUpdateRequest } from '../lib/api/profile';

/**
 * Fetch current user's profile
 * 
 * Query Key: ['profile']
 * Returns: User profile data
 */
export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const response = await profileApi.getProfile();
      return response.profile;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - profile doesn't change frequently
  });
}

/**
 * Update current user's profile
 * 
 * On success: Invalidates ['profile'] and ['me'] to refresh data
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: ProfileUpdateRequest) => {
      const response = await profileApi.updateProfile(data);
      return response.profile;
    },
    onSuccess: (updatedProfile) => {
      // Update the profile cache
      queryClient.setQueryData(['profile'], updatedProfile);
      // Invalidate and refetch 'me' immediately to update top nav
      queryClient.invalidateQueries({ 
        queryKey: ['me'],
        refetchType: 'active' // Force immediate refetch of active queries
      });
    },
  });
}

/**
 * Remove profile image
 * 
 * On success: Invalidates ['profile'] to refresh data
 */
export function useRemoveProfileImage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: profileApi.removeProfileImage,
    onSuccess: () => {
      // Invalidate profile to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      // Force immediate refetch of 'me' to update top nav
      queryClient.invalidateQueries({ 
        queryKey: ['me'],
        refetchType: 'active'
      });
    },
  });
}

/**
 * Fetch public profile by user ID
 * 
 * Query Key: ['publicProfile', userId]
 * Returns: Public user profile data
 */
export function usePublicProfile(userId: string) {
  return useQuery({
    queryKey: ['publicProfile', userId],
    queryFn: async () => {
      // Import usersApi dynamically to avoid circular dependency
      const { usersApi } = await import('../lib/api');
      const response = await usersApi.getPublicProfile(userId);
      return response.profile;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes - public profiles don't change frequently
  });
}
